import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Flex,
  Progress,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react-hooks';
import { IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { createClientAndEpisode } from '../../../lib/adminActionsService';
import { config } from '../../../config';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { isStaffAddClientSelfAssignRole } from '../../../lib/staffAddClientPermissions';
import { staffClientEpisodeSchema } from '../../../schemas/staff-client-episode.schema';
import { intakeReducer } from '../../public/intake/intakeReducer';
import { isStepValid } from '../../public/intake/validation';
import { StepZip } from '../../public/intake/steps/StepZip';
import {
  getStaffAddClientInitialState,
  isStaffAddClientWideStep,
  STAFF_ADD_CLIENT_STEP_TITLES,
  STAFF_ADD_CLIENT_STEPS,
  type StaffAddClientIntakeStep,
} from '../../public/intake/staffAddClientFlow';
import { formatUserProvisioningError } from '../../../lib/userProvisioningErrors';
import { listPracticeIntakeProviders } from '../../public/intake/listPracticeIntakeProviders';
import { useEpisodeOfCareTypes } from '../../calendar/episodeOfCareTypeCatalog';
import type { MockProvider } from '../../public/intake/mockProviders';
import type { Practice, User } from '../types/rbac';
import '../../public/intake/intake.css';

interface StaffAddClientFlowProps {
  practice: Practice;
  onClose: () => void;
  onClientCreated?: (user: User, clientName: string, context: { organizationId: string }) => void;
  onStepChange?: (step: number) => void;
}

export function StaffAddClientFlow(props: StaffAddClientFlowProps): JSX.Element {
  return <StaffAddClientFlowInner {...props} />;
}

function StaffAddClientFlowInner({
  practice,
  onClose,
  onClientCreated,
  onStepChange,
}: StaffAddClientFlowProps): JSX.Element {
  const medplum = useMedplum();
  const { role } = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const { types: episodeTypes, loading: episodeTypesLoading } = useEpisodeOfCareTypes();
  const selfAssign = isStaffAddClientSelfAssignRole(role);

  const [state, dispatch] = useReducer(intakeReducer, undefined, getStaffAddClientInitialState);

  const submitInFlightRef = useRef(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const [stepActionLoading, setStepActionLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [providers, setProviders] = useState<MockProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [practitionerId, setPractitionerId] = useState<string | null>(null);
  const [therapyTypeCode, setTherapyTypeCode] = useState<string | null>(null);
  const [paymentTypeCode, setPaymentTypeCode] = useState<'insurance' | 'self-pay' | null>(null);

  const { answers, activeStep } = state;
  const wizardStepIndex = STAFF_ADD_CLIENT_STEPS.indexOf(activeStep as StaffAddClientIntakeStep);
  const totalWizardSteps = STAFF_ADD_CLIENT_STEPS.length;
  const isSingleStepFlow = totalWizardSteps === 1;

  const assignedPractitionerId = selfAssign ? practitioner?.id : practitionerId;
  const canSubmit =
    isStepValid(activeStep, answers) &&
    Boolean(assignedPractitionerId && therapyTypeCode && paymentTypeCode) &&
    !practitionerLoading &&
    !providersLoading &&
    !episodeTypesLoading;

  useEffect(() => {
    if (selfAssign) {
      setProviders([]);
      return;
    }
    let cancelled = false;
    setProvidersLoading(true);
    void listPracticeIntakeProviders(medplum, practice.id)
      .then((result) => {
        if (!cancelled) setProviders(result);
      })
      .catch(() => {
        if (!cancelled) setSubmitError('Could not load providers for this practice.');
      })
      .finally(() => {
        if (!cancelled) setProvidersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, practice.id, selfAssign]);

  useEffect(() => {
    onStepChange?.(activeStep);
  }, [activeStep, onStepChange]);

  const handleConfirm = useCallback(async () => {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setStepActionLoading(true);
    setSubmitError(null);
    try {
      const input = staffClientEpisodeSchema.parse({
        firstName: answers.firstName,
        lastName: answers.lastName,
        email: answers.email,
        dateOfBirth: answers.dateOfBirth,
        zip: answers.zip,
        organizationId: practice.id,
        practitionerId: assignedPractitionerId,
        therapyTypeCode,
        paymentTypeCode,
      });
      idempotencyKeyRef.current ??= crypto.randomUUID();
      const result = await createClientAndEpisode(medplum, {
        ...input,
        idempotencyKey: idempotencyKeyRef.current,
        projectId: config.medplum.projectId,
      });
      medplum.invalidateSearches('Patient');

      const clientName = `${answers.firstName} ${answers.lastName}`;
      const user: User = {
        id: result.patientId,
        name: clientName,
        email: answers.email,
        role: 'client',
        practiceId: practice.id,
        practiceName: practice.name,
        status: 'Active',
        createdAt: new Date().toISOString(),
        lastActive: 'Never',
      };
      onClientCreated?.(user, clientName, { organizationId: practice.id });
      notifications.show({
        title: 'Client added',
        message: `${clientName} was added to ${practice.name}.`,
        color: 'green',
      });
    } catch (err) {
      console.error('[StaffAddClient] submission failed:', err);
      const msg = `${formatUserProvisioningError(err)} Retry to finish setup. If the client is already on the roster, open their profile and use Add episode of care.`;
      setSubmitError(msg);
      notifications.show({
        color: 'red',
        title: 'Could not add client',
        message: msg,
      });
    } finally {
      submitInFlightRef.current = false;
      setStepActionLoading(false);
    }
  }, [
    answers,
    assignedPractitionerId,
    medplum,
    onClientCreated,
    paymentTypeCode,
    practice.id,
    practice.name,
    therapyTypeCode,
  ]);

  const stepTitle = STAFF_ADD_CLIENT_STEP_TITLES[activeStep as StaffAddClientIntakeStep] ?? '';
  const headerTitle = isSingleStepFlow
    ? `Add client — ${practice.name}`
    : `Add client — ${practice.name} · Step ${wizardStepIndex + 1} of ${totalWizardSteps}`;

  const isWideStep = isStaffAddClientWideStep(activeStep);

  return (
    <Box
      className="intake-flow intake-flow--in-modal intake-flow--staff-add-client"
      data-wide={isWideStep || undefined}
    >
      <Flex className="intake-flow-header" justify="space-between" align="center">
        <Text size="sm" fw={600} c="dimmed">
          {headerTitle}
        </Text>
        <ActionIcon variant="subtle" size="lg" onClick={onClose} aria-label="Close">
          <IconX size={20} />
        </ActionIcon>
      </Flex>

      {submitError && (
        <Alert color="red" variant="light" radius="md" title="Something went wrong" mx="md" mt="xs">
          {submitError}
        </Alert>
      )}

      <Box className="intake-flow-main">
        <Box key={activeStep} className="intake-step-enter">
          <Text className="intake-step-title" fw={700} mb="md">
            {stepTitle}
          </Text>
          <StepZip
            answers={answers}
            dispatch={dispatch}
            introText="Enter the client's contact details, then choose the episode of care to create."
          />
          <Stack gap="sm" mt="lg">
            {!selfAssign ? (
              <Select
                label="Provider"
                placeholder="Choose a provider"
                data={providers.map((provider) => ({ value: provider.id, label: provider.name }))}
                value={practitionerId}
                onChange={setPractitionerId}
                searchable
                required
                disabled={providersLoading}
              />
            ) : null}
            <Select
              label="Episode of care type"
              placeholder="Choose an episode type"
              data={episodeTypes
                .filter((type) => type.active)
                .map((type) => ({ value: type.code, label: type.name }))}
              value={therapyTypeCode}
              onChange={setTherapyTypeCode}
              searchable
              required
              disabled={episodeTypesLoading}
            />
            <Select
              label="Payment type"
              placeholder="Choose a payment type"
              data={[
                { value: 'insurance', label: 'Insurance' },
                { value: 'self-pay', label: 'Self-pay' },
              ]}
              value={paymentTypeCode}
              onChange={(value) =>
                setPaymentTypeCode(value === 'insurance' || value === 'self-pay' ? value : null)
              }
              required
            />
          </Stack>
        </Box>
      </Box>

      <Box className="intake-flow-footer">
        <Flex justify="space-between" align="center" wrap="wrap" gap="md">
          <Button variant="default" onClick={onClose} disabled={stepActionLoading}>
            Cancel
          </Button>
          <Button
            color="gold"
            disabled={!canSubmit || stepActionLoading}
            loading={stepActionLoading}
            onClick={() => void handleConfirm()}
          >
            Add client
          </Button>
        </Flex>
        {!isSingleStepFlow ? (
          <>
            <Progress
              value={((wizardStepIndex + 1) / totalWizardSteps) * 100}
              size="sm"
              mt="sm"
              color="gold"
            />
            <Text size="xs" c="dimmed" mt={4}>
              {wizardStepIndex + 1} of {totalWizardSteps} steps
            </Text>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
